import React, { useState, useRef, useCallback } from 'react';
import ReactPlayer from 'react-player';
import {
  Container,
  MobileCircleImage,
  MobileCircleOverlay,
  MobileCircleWrapper,
  MobileConnectorLine,
  MobileEraItem,
  MobileEraRow,
  MobileLabel,
  MobileList,
  MobileVideoPanel,
  SVGWrapper,
  VideoPopup,
} from './styles';

export interface IEraVideoItem {
  /** Era circle index: 0-4 from left to right */
  index: number;
  /** YouTube / any URL supported by react-player */
  videoUrl: string;
  label?: string;
  /** URL or imported image to fill the circle */
  image?: string;
}

interface ITecnologyEraCardProps {
  eras: IEraVideoItem[];
}

/**
 * Converts YouTube Shorts URLs to standard watch URLs so ReactPlayer can embed them.
 * e.g. https://youtube.com/shorts/VIDEO_ID?feature=share → https://www.youtube.com/watch?v=VIDEO_ID
 */
function normalizeYouTubeUrl(url: string): string {
  const shortsMatch = url.match(
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([A-Za-z0-9_-]+)/,
  );
  if (shortsMatch) {
    return `https://www.youtube.com/watch?v=${shortsMatch[1]}`;
  }
  return url;
}

/**
 * X-position of each circle center as percentage of the SVG viewBox width (1213px).
 * Circles are at cx = 64, 319, 571, 823, 1078.
 */
const ERA_POSITIONS_PERCENT: number[] = [
  (64 / 1213) * 100,
  (319 / 1213) * 100,
  (571 / 1213) * 100,
  (823 / 1213) * 100,
  (1078 / 1213) * 100,
];

const TecnologyEraCard: React.FC<ITecnologyEraCardProps> = ({ eras }) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getEra = useCallback(
    (index: number) => eras.find(e => e.index === index),
    [eras],
  );

  const handleMouseEnter = (index: number) => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    // Only show hover popup when no era is active, or when hovering a different one
    if (activeIndex === null) {
      setHoveredIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (activeIndex === null) {
      leaveTimerRef.current = setTimeout(() => {
        setHoveredIndex(null);
      }, 150);
    }
  };

  const handleClick = (index: number) => {
    if (!getEra(index)) return;

    if (activeIndex === index) {
      // Clicking the active circle deactivates it
      setActiveIndex(null);
      setHoveredIndex(null);
    } else {
      // Switch to this circle
      setActiveIndex(index);
      setHoveredIndex(null);
    }
  };

  // Which index is currently showing a popup
  const visibleIndex = activeIndex !== null ? activeIndex : hoveredIndex;
  const visibleEra = visibleIndex !== null ? getEra(visibleIndex) : null;

  return (
    <Container>
      <SVGWrapper>
        {/* Inline SVG so we can attach React event handlers to each circle group */}
        <svg
          width="100%"
          viewBox="0 0 1213 235"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: 'block' }}
        >
          {/* ── Image patterns for circle fills ── */}
          <defs>
            {([64, 319, 571, 823, 1078] as const).map((cx, idx) => {
              const era = getEra(idx);
              if (!era?.image) return null;
              const cy = idx === 0 ? 163 : 163.389;
              return (
                <pattern
                  key={idx}
                  id={`era-img-${idx}`}
                  patternUnits="userSpaceOnUse"
                  x={cx - 50}
                  y={cy - 50}
                  width={100}
                  height={100}
                >
                  <image
                    href={era.image}
                    x={0}
                    y={0}
                    width={100}
                    height={100}
                    preserveAspectRatio="xMidYMid slice"
                  />
                </pattern>
              );
            })}
          </defs>

          {/* ── static SVG paths (wave, connector lines, labels) ── */}
          <path
            d="M1141.19 169.735C1140.95 205.574 1111.66 234.537 1075.91 234.292C1059.62 234.181 1044.05 227.902 1032.04 216.607C1032 216.577 1031.95 216.543 1031.91 216.503C1018.69 204.121 1011.19 186.654 1011.31 168.583C1011.34 164.69 1010.99 160.751 1010.27 156.868C1004.89 127.884 979.635 106.697 950.217 106.496C920.8 106.295 895.258 127.135 889.485 156.043C888.711 159.916 888.307 163.855 888.28 167.743C888.036 203.582 858.751 232.545 822.995 232.3C787.239 232.056 758.353 202.701 758.598 166.857C758.598 166.832 758.598 166.793 758.603 166.754C758.781 132.802 731.381 104.997 697.503 104.765C668.085 104.564 642.544 125.404 636.771 154.312C635.996 158.185 635.593 162.124 635.566 166.012C635.322 201.851 606.037 230.814 570.281 230.57C534.525 230.325 505.639 200.97 505.884 165.126C505.91 161.228 505.556 157.289 504.839 153.411C499.461 124.422 474.207 103.24 444.789 103.039C415.371 102.838 389.83 123.678 384.057 152.586C383.282 156.454 382.879 160.393 382.852 164.286C382.598 200.135 353.313 229.093 317.562 228.848C281.811 228.604 252.92 199.249 253.165 163.405C253.191 159.512 252.842 155.573 252.12 151.69C246.742 122.706 221.488 101.519 192.07 101.318C162.652 101.117 137.111 121.957 131.338 150.865C130.563 154.738 130.16 158.677 130.133 162.565C129.884 198.409 100.594 227.367 64.8432 227.122C29.0919 226.878 0.200939 197.523 0.445708 161.684C0.45197 160.767 1.20048 160.022 2.11994 160.028C3.0394 160.035 3.7777 160.785 3.77141 161.707C3.5343 195.707 30.9438 223.562 64.8659 223.793C98.7881 224.025 126.571 196.547 126.803 162.547C126.823 159.537 127.069 156.44 127.531 153.344C132.267 121.566 160.019 97.7702 192.088 97.9892C224.157 98.2083 251.581 122.381 255.883 154.221C256.302 157.322 256.506 160.422 256.485 163.433C256.253 197.438 283.663 225.288 317.585 225.519C351.507 225.751 379.289 198.273 379.522 164.273C379.542 161.263 379.788 158.171 380.25 155.07C384.986 123.292 412.738 99.4962 444.807 99.7152C476.876 99.9342 504.3 124.107 508.602 155.947C509.021 159.048 509.225 162.143 509.204 165.159C508.972 199.164 536.382 227.014 570.299 227.245C604.216 227.477 632.008 199.999 632.241 165.999C632.261 162.989 632.507 159.892 632.969 156.796C637.705 125.018 665.457 101.222 697.526 101.441C733.282 101.685 762.168 131.041 761.923 166.885C761.923 166.914 761.923 166.953 761.918 166.992C761.745 200.944 789.14 228.74 823.018 228.971C856.896 229.203 884.722 201.725 884.955 167.725C884.975 164.715 885.221 161.618 885.683 158.522C890.419 126.744 918.171 102.948 950.24 103.167C982.309 103.386 1009.73 127.559 1014.04 159.399C1014.45 162.5 1014.66 165.6 1014.64 168.611C1014.52 185.53 1021.14 201.368 1033.28 213.208C1033.35 213.277 1033.41 213.356 1033.45 213.439C1044.92 224.628 1060 230.85 1075.94 230.959C1109.86 231.19 1137.65 203.713 1137.88 169.712C1137.89 168.795 1138.64 168.05 1139.56 168.057C1140.48 168.063 1141.21 168.813 1141.21 169.735L1141.19 169.735Z"
            fill="#4CAF50"
          />

          {/* Orange connector dots */}
          <path
            d="M137.223 151.266C136.596 147.198 132.798 144.41 128.741 145.039C124.683 145.668 121.902 149.476 122.529 153.543C123.157 157.611 126.954 160.398 131.012 159.769C135.07 159.14 137.851 155.333 137.223 151.266Z"
            fill="#F39519"
          />
          <path
            d="M137.706 152.467C137.736 148.143 134.248 144.599 129.934 144.569C125.621 144.54 122.085 148.036 122.056 152.36C122.026 156.685 125.514 160.229 129.827 160.259C134.141 160.288 137.677 156.792 137.706 152.467ZM122.843 152.366C122.87 148.473 126.051 145.332 129.929 145.359C133.808 145.385 136.945 148.574 136.919 152.462C136.892 156.35 133.711 159.496 129.833 159.469C125.954 159.443 122.816 156.254 122.843 152.366Z"
            fill="#F39519"
          />
          <path
            d="M256.883 146.389C253.095 144.802 248.74 146.593 247.156 150.39C245.572 154.188 247.359 158.553 251.147 160.141C254.935 161.729 259.29 159.937 260.874 156.14C262.458 152.343 260.671 147.977 256.883 146.389Z"
            fill="#F39519"
          />
          <path
            d="M261.824 153.316C261.854 148.992 258.366 145.447 254.053 145.418C249.739 145.389 246.203 148.885 246.174 153.209C246.144 157.533 249.632 161.078 253.945 161.107C258.259 161.137 261.795 157.64 261.824 153.316ZM246.961 153.215C246.988 149.322 250.169 146.181 254.047 146.207C257.926 146.234 261.064 149.423 261.037 153.311C261.01 157.199 257.829 160.344 253.951 160.318C250.072 160.291 246.935 157.102 246.961 153.215Z"
            fill="#F39519"
          />
          <path
            d="M386.354 147.276C382.566 145.688 378.211 147.48 376.627 151.277C375.043 155.074 376.83 159.44 380.618 161.028C384.406 162.615 388.761 160.824 390.345 157.027C391.929 153.229 390.142 148.864 386.354 147.276Z"
            fill="#F39519"
          />
          <path
            d="M391.288 154.199C391.318 149.874 387.83 146.33 383.517 146.301C379.203 146.271 375.667 149.768 375.638 154.092C375.608 158.416 379.096 161.961 383.409 161.99C387.723 162.019 391.259 158.523 391.288 154.199ZM376.425 154.097C376.452 150.204 379.633 147.064 383.511 147.09C387.39 147.117 390.527 150.305 390.501 154.193C390.474 158.081 387.293 161.227 383.415 161.201C379.536 161.174 376.398 157.985 376.425 154.097Z"
            fill="#F39519"
          />
          <path
            d="M514.09 155.038C514.118 150.922 510.813 147.563 506.707 147.535C502.601 147.507 499.25 150.82 499.222 154.936C499.194 159.052 502.499 162.411 506.605 162.439C510.711 162.467 514.062 159.154 514.09 155.038Z"
            fill="#F39519"
          />
          <path
            d="M514.482 155.041C514.511 150.716 511.024 147.172 506.71 147.143C502.396 147.113 498.86 150.609 498.831 154.934C498.801 159.258 502.289 162.802 506.603 162.832C510.916 162.861 514.452 159.365 514.482 155.041ZM499.618 154.939C499.645 151.046 502.826 147.905 506.704 147.932C510.583 147.958 513.721 151.147 513.694 155.035C513.668 158.923 510.487 162.069 506.608 162.042C502.73 162.016 499.592 158.827 499.618 154.939Z"
            fill="#F39519"
          />
          <path
            d="M638.57 149.008C634.782 147.421 630.427 149.212 628.843 153.009C627.259 156.807 629.046 161.172 632.834 162.76C636.622 164.348 640.976 162.556 642.56 158.759C644.144 154.962 642.358 150.596 638.57 149.008Z"
            fill="#F39519"
          />
          <path
            d="M643.487 155.922C643.516 151.598 640.028 148.054 635.715 148.024C631.401 147.995 627.865 151.491 627.836 155.816C627.806 160.14 631.294 163.684 635.608 163.714C639.921 163.743 643.457 160.247 643.487 155.922ZM628.623 155.821C628.65 151.928 631.831 148.787 635.709 148.814C639.588 148.84 642.726 152.029 642.699 155.917C642.673 159.805 639.491 162.951 635.613 162.924C631.735 162.898 628.597 159.709 628.623 155.821Z"
            fill="#F39519"
          />
          <path
            d="M766.735 156.763C766.763 152.648 763.457 149.288 759.351 149.26C755.246 149.232 751.894 152.546 751.866 156.662C751.838 160.778 755.144 164.137 759.25 164.165C763.355 164.193 766.706 160.879 766.735 156.763Z"
            fill="#F39519"
          />
          <path
            d="M767.125 156.766C767.155 152.442 763.667 148.898 759.353 148.868C755.04 148.839 751.504 152.335 751.474 156.659C751.445 160.984 754.933 164.528 759.246 164.557C763.56 164.587 767.096 161.091 767.125 156.766ZM752.262 156.665C752.288 152.772 755.47 149.631 759.348 149.658C763.226 149.684 766.364 152.873 766.338 156.761C766.311 160.649 763.13 163.794 759.252 163.768C755.373 163.742 752.235 160.553 752.262 156.665Z"
            fill="#F39519"
          />
          <path
            d="M891.23 150.741C887.442 149.153 883.087 150.944 881.503 154.742C879.919 158.539 881.706 162.905 885.494 164.492C889.282 166.08 893.637 164.289 895.221 160.491C896.804 156.694 895.018 152.329 891.23 150.741Z"
            fill="#F39519"
          />
          <path
            d="M896.13 157.647C896.16 153.323 892.672 149.778 888.358 149.749C884.045 149.72 880.509 153.216 880.479 157.54C880.45 161.865 883.937 165.409 888.251 165.438C892.565 165.468 896.101 161.971 896.13 157.647ZM881.267 157.546C881.293 153.653 884.474 150.512 888.353 150.538C892.231 150.565 895.369 153.754 895.343 157.642C895.316 161.53 892.135 164.675 888.257 164.649C884.378 164.622 881.24 161.434 881.267 157.546Z"
            fill="#F39519"
          />
          <path
            d="M1015.05 151.589C1011.26 150.001 1006.91 151.792 1005.33 155.59C1003.74 159.387 1005.53 163.752 1009.32 165.34C1013.1 166.928 1017.46 165.136 1019.04 161.339C1020.63 157.542 1018.84 153.176 1015.05 151.589Z"
            fill="#F39519"
          />
          <path
            d="M1019.94 158.493C1019.97 154.168 1016.49 150.624 1012.17 150.595C1007.86 150.565 1004.32 154.062 1004.29 158.386C1004.26 162.71 1007.75 166.254 1012.07 166.284C1016.38 166.313 1019.92 162.817 1019.94 158.493ZM1005.08 158.391C1005.11 154.498 1008.29 151.358 1012.17 151.384C1016.05 151.411 1019.18 154.599 1019.16 158.487C1019.13 162.375 1015.95 165.521 1012.07 165.495C1008.19 165.468 1005.05 162.279 1005.08 158.391Z"
            fill="#F39519"
          />

          {/* Era labels text */}
          {[64, 319, 571, 823, 1078].map((cx, idx) => {
            const era = getEra(idx);
            return (
              <text
                key={idx}
                x={cx}
                y={46}
                textAnchor="middle"
                dominantBaseline="auto"
                fontFamily="RedHatDisplay-Bold"
                fill="#323232"
                fontSize={18}
              >
                {era?.label ?? String(idx + 1)}
              </text>
            );
          })}

          {/* Orange vertical connector lines + arrowheads */}
          <path
            d="M112.691 126.84C101.675 111.35 83.789 102.025 64.8325 101.895C45.876 101.766 27.8151 110.87 16.5929 126.252L22.1213 130.306C32.05 116.699 48.0006 108.649 64.7905 108.764C81.5803 108.879 97.3756 117.125 107.118 130.832L112.691 126.85L112.691 126.84Z"
            fill="#FF9800"
          />
          <path
            d="M63.9459 56.6114L63.6162 104.879L66.3518 104.898L66.6815 56.6301L63.9459 56.6114Z"
            fill="#F39519"
          />
          <path
            d="M367.691 127.229C356.675 111.738 338.789 102.413 319.832 102.284C300.876 102.154 282.815 111.258 271.593 126.641L277.121 130.694C287.05 117.088 303.001 109.038 319.79 109.153C336.58 109.267 352.376 117.514 362.118 131.221L367.691 127.238L367.691 127.229Z"
            fill="#FF9800"
          />
          <path
            d="M318.946 57.0001L318.616 105.268L321.352 105.286L321.681 57.0188L318.946 57.0001Z"
            fill="#F39519"
          />
          <path
            d="M619.691 127.229C608.675 111.738 590.789 102.413 571.832 102.284C552.876 102.154 534.815 111.258 523.593 126.641L529.121 130.694C539.05 117.088 555.001 109.038 571.79 109.153C588.58 109.267 604.376 117.514 614.118 131.221L619.691 127.238L619.691 127.229Z"
            fill="#FF9800"
          />
          <path
            d="M570.946 57.0001L570.616 105.268L573.352 105.286L573.681 57.0188L570.946 57.0001Z"
            fill="#F39519"
          />
          <path
            d="M871.691 127.229C860.675 111.738 842.789 102.413 823.832 102.284C804.876 102.154 786.815 111.258 775.593 126.641L781.121 130.694C791.05 117.088 807.001 109.038 823.79 109.153C840.58 109.267 856.376 117.514 866.118 131.221L871.691 127.238L871.691 127.229Z"
            fill="#FF9800"
          />
          <path
            d="M822.946 57.0001L822.616 105.268L825.352 105.286L825.681 57.0188L822.946 57.0001Z"
            fill="#F39519"
          />
          <path
            d="M1126.69 127.229C1115.68 111.738 1097.79 102.413 1078.83 102.284C1059.88 102.154 1041.82 111.258 1030.59 126.641L1036.12 130.694C1046.05 117.088 1062 109.038 1078.79 109.153C1095.58 109.267 1111.38 117.514 1121.12 131.221L1126.69 127.238L1126.69 127.229Z"
            fill="#FF9800"
          />
          <path
            d="M1077.95 57.0001L1077.62 105.268L1080.35 105.286L1080.68 57.0188L1077.95 57.0001Z"
            fill="#F39519"
          />

          {/* ── Interactive circle groups ── */}
          {ERA_POSITIONS_PERCENT.map((leftPercent, idx) => {
            const era = getEra(idx);
            const hasVideo = Boolean(era);
            const _isHovered = hoveredIndex === idx;
            const isActiveCircle = activeIndex === idx;

            // SVG cx values for the 5 circles
            const cxValues = [64, 319, 571, 823, 1078];
            const cyValues = [163, 163.389, 163.389, 163.389, 163.389];
            const cx = cxValues[idx];
            const cy = cyValues[idx];

            return (
              <g
                key={idx}
                style={{ cursor: hasVideo ? 'pointer' : 'default' }}
                onMouseEnter={() => hasVideo && handleMouseEnter(idx)}
                onMouseLeave={() => hasVideo && handleMouseLeave()}
                onClick={() => hasVideo && handleClick(idx)}
              >
                {/* Main circle */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={50}
                  fill={
                    era?.image
                      ? `url(#era-img-${idx})`
                      : isActiveCircle
                        ? '#1a3a1a'
                        : 'black'
                  }
                  stroke={isActiveCircle ? '#4CAF50' : '#FFFFF'}
                  strokeWidth={3}
                  style={{ transition: 'stroke 0.2s ease' }}
                />
                {/* Darkening overlay on top of the image */}
                {era?.image && (
                  <circle cx={cx} cy={cy} r={50} fill="rgba(0,0,0,0.2)" />
                )}
                {/* Inner play-button circle outline */}
                <circle
                  cx={cx}
                  cy={cy}
                  r={29.73}
                  stroke={isActiveCircle ? '#4CAF50' : '#EEEEEE'}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
                {/* Play triangle */}
                <path
                  d={`M${cx - 5.947} ${cy - 11.892}L${cx + 11.892} ${cy}L${cx - 5.947} ${cy + 11.892}V${cy - 11.892}Z`}
                  stroke={isActiveCircle ? '#4CAF50' : '#EEEEEE'}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </g>
            );
          })}
        </svg>

        {/* Video popup – rendered in HTML so react-player works normally */}
        {visibleEra && visibleIndex !== null && (
          <VideoPopup
            $leftPercent={ERA_POSITIONS_PERCENT[visibleIndex]}
            onMouseEnter={() => {
              if (leaveTimerRef.current) {
                clearTimeout(leaveTimerRef.current);
                leaveTimerRef.current = null;
              }
            }}
            onMouseLeave={() => {
              if (activeIndex === null) {
                leaveTimerRef.current = setTimeout(() => {
                  setHoveredIndex(null);
                }, 150);
              }
            }}
          >
            <ReactPlayer
              url={normalizeYouTubeUrl(visibleEra.videoUrl)}
              width={300}
              height={400}
              playing
              muted
              controls
              config={{
                youtube: {
                  playerVars: { modestbranding: 1 },
                },
              }}
            />
          </VideoPopup>
        )}
      </SVGWrapper>

      {/* ── Mobile layout ── */}
      <MobileList>
        {[0, 1, 2, 3, 4].map(idx => {
          const era = getEra(idx);
          if (!era) return null;
          const isActiveCircle = activeIndex === idx;
          return (
            <MobileEraItem key={idx}>
              <MobileEraRow type="button" onClick={() => handleClick(idx)}>
                <MobileCircleWrapper $isActive={isActiveCircle}>
                  <MobileCircleImage $image={era.image} />
                  {era.image && <MobileCircleOverlay />}
                  <svg
                    viewBox="0 0 50 50"
                    width={24}
                    height={24}
                    style={{ position: 'relative', zIndex: 1 }}
                  >
                    <circle
                      cx={25}
                      cy={25}
                      r={22}
                      fill="none"
                      stroke={isActiveCircle ? '#4CAF50' : '#EEEEEE'}
                      strokeWidth={2}
                    />
                    <polygon
                      points="21,16 37,25 21,34"
                      fill="none"
                      stroke={isActiveCircle ? '#4CAF50' : '#EEEEEE'}
                      strokeWidth={2}
                      strokeLinejoin="round"
                    />
                  </svg>
                </MobileCircleWrapper>
                <MobileLabel>{era.label ?? String(idx + 1)}</MobileLabel>
              </MobileEraRow>
              {isActiveCircle && (
                <MobileVideoPanel>
                  <ReactPlayer
                    url={normalizeYouTubeUrl(era.videoUrl)}
                    width="100%"
                    height={200}
                    playing
                    muted
                    controls
                    config={{
                      youtube: { playerVars: { modestbranding: 1 } },
                    }}
                  />
                </MobileVideoPanel>
              )}
              {idx < 4 && <MobileConnectorLine />}
            </MobileEraItem>
          );
        })}
      </MobileList>
    </Container>
  );
};

export default TecnologyEraCard;
